/**
 * 图案叠加的内置程序化图案（DESIGN.md §7.1 补充）。
 *
 * 真实 Photoshop 的"图案叠加"允许挑选/导入任意图案；这个代码库没有图案
 * 资产系统（导入/管理 UI、素材库），本次简化成一组内置的纯数学图案——
 * 这是"图层样式"这次扩展里唯一的功能简化，在完成报告里会说明。
 */

export const BUILT_IN_PATTERN_IDS = ['stripes', 'dots', 'checkerboard', 'grid', 'crosshatch'] as const;

export type BuiltInPatternId = (typeof BUILT_IN_PATTERN_IDS)[number];

const PATTERN_LABELS: Readonly<Record<BuiltInPatternId, string>> = {
  stripes: '条纹',
  dots: '点阵',
  checkerboard: '棋盘格',
  grid: '网格',
  crosshatch: '十字网格',
};

export function patternLabel(id: string): string {
  return (PATTERN_LABELS as Readonly<Record<string, string>>)[id] ?? '条纹';
}

function isBuiltInPatternId(id: string): id is BuiltInPatternId {
  return (BUILT_IN_PATTERN_IDS as readonly string[]).includes(id);
}

/**
 * 该点在图案里的覆盖度（0 或 1——图案边缘不做抗锯齿，简化）。
 * `scale` 是图案整体缩放（1=基础周期 16 文档像素），未识别的 id 回退
 * "条纹"，不抛错——效果预览不该因为脏参数崩溃。
 */
export function patternCoverageAt(id: string, x: number, y: number, scale: number): number {
  const period = Math.max(1, scale) * 16;
  const resolved = isBuiltInPatternId(id) ? id : 'stripes';
  const px = x / period;
  const py = y / period;

  switch (resolved) {
    case 'dots': {
      const fx = px - Math.floor(px) - 0.5;
      const fy = py - Math.floor(py) - 0.5;
      return fx * fx + fy * fy < 0.06 ? 1 : 0;
    }
    case 'checkerboard':
      return (Math.floor(px) + Math.floor(py)) % 2 === 0 ? 1 : 0;
    case 'grid': {
      const fx = px - Math.floor(px);
      const fy = py - Math.floor(py);
      return fx < 0.08 || fy < 0.08 ? 1 : 0;
    }
    case 'crosshatch': {
      const sum = px + py;
      const diff = px - py;
      const df1 = sum - Math.floor(sum);
      const df2 = diff - Math.floor(diff);
      return df1 < 0.08 || df2 < 0.08 ? 1 : 0;
    }
    case 'stripes': {
      const fx = px - Math.floor(px);
      return fx < 0.5 ? 1 : 0;
    }
  }
}
